import {
  fetch_manual_verification_history,
  fetch_manual_verification_run
} from '@/services/onboarding_journey_service';
import {
  format_detail_value
} from '@/views/onboarding_system_views/Gates/Verification/manage_verification_helpers';
import { manageVerificationSupportMethods } from '@/views/onboarding_system_views/Gates/Verification/manage_verification_support_methods';
import { APPLICATION_REFERENCE_TAB_ID } from './queue_form_method_shared';

export const queueFormHistoryMethods = {
  filter_case_journey_steps(steps = [], current_gate_id = '') {
    const source_steps = Array.isArray(steps) ? steps : [];
    if (!source_steps.length) return [];

    const normalized_current_gate_id = String(current_gate_id || '').trim().toLowerCase();
    const current_index = source_steps.findIndex((step) => (
      String(step?.gate_id || '').trim().toLowerCase() === normalized_current_gate_id
    ));
    if (current_index < 0) return source_steps;

    return source_steps.map((step, index) => ({
      ...step,
      state: index < current_index ? 'completed' : index === current_index ? 'current' : 'upcoming'
    }));
  },
  async set_workflow_content_tab(step = {}) {
    const target_gate_id = String(step?.gate_id || '').trim();
    if (!target_gate_id) {
      this.show_current_workflow_step();
      return;
    }
    if (target_gate_id === APPLICATION_REFERENCE_TAB_ID) {
      this.active_history_gate_id = APPLICATION_REFERENCE_TAB_ID;
      this.active_history_tab = 'summary';
      this.history_panel_error = '';
      return;
    }
    if (step?.state === 'current') {
      this.show_current_workflow_step();
      return;
    }
    if (!['completed', 'previous'].includes(String(step?.state || '').trim().toLowerCase())) return;

    this.active_history_gate_id = target_gate_id;
    this.active_history_tab = 'summary';
    this.history_panel_error = '';
    await this.load_history_panel(target_gate_id);
  },
  async open_workflow_step(step = {}) {
    await this.set_workflow_content_tab(step);
  },
  show_current_workflow_step() {
    this.active_history_gate_id = '';
    this.active_history_tab = 'summary';
    this.history_panel_error = '';
  },
  async set_workflow_shell_tab(tab_id = 'action') {
    if (tab_id === 'reference') {
      await this.set_workflow_content_tab(this.workflow_reference_tab || {});
      return;
    }
    if (tab_id === 'history') {
      const first_history_step = this.workflow_history_tabs[0] || null;
      if (!first_history_step) {
        this.active_history_gate_id = '__history__';
        this.active_history_tab = 'summary';
        this.history_panel_error = '';
        return;
      }
      await this.set_workflow_content_tab(first_history_step);
      return;
    }
    this.show_current_workflow_step();
  },
  async load_history_panel(target_gate_id = '') {
    this.history_panel_loading = false;
    this.history_panel_error = '';
    if (target_gate_id === 'manual-verification') {
      await this.load_manual_history_panel();
    }
  },
  async load_manual_history_panel() {
    const entity_profile_id = Number(
      this.hydrated_case_detail?.entity_profile_id
      || this.selected_application_form_meta?.entity_profile_id
      || 0
    ) || null;
    if (!entity_profile_id) {
      this.history_panel_error = 'Unable to load manual verification history for this case.';
      this.history_manual_run = null;
      return;
    }

    this.history_panel_loading = true;
    this.history_panel_error = '';
    try {
      const history_rows = await fetch_manual_verification_history(entity_profile_id);
      const latest_manual_row = (Array.isArray(history_rows) ? history_rows : [])
        .filter((row) => String(row?.mode || '').trim().toLowerCase() === 'manual')
        .sort((left, right) => Number(right?.run_id || 0) - Number(left?.run_id || 0))[0] || null;
      if (!latest_manual_row?.run_id) {
        this.history_manual_run = null;
        this.history_panel_error = 'No completed manual verification history was found for this case.';
        return;
      }
      this.history_manual_run = await fetch_manual_verification_run(latest_manual_row.run_id);
    } catch (error) {
      this.history_manual_run = null;
      this.history_panel_error = error?.response?.data?.message || error?.message || 'Unable to load manual verification history.';
    } finally {
      this.history_panel_loading = false;
    }
  },
  set_history_tab(tab_id = 'summary') {
    this.active_history_tab = String(tab_id || 'summary').trim() || 'summary';
  },
  format_history_value(value) {
    if (value === undefined || value === null || value === '') return '-';
    if (typeof value !== 'object') return String(value);
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  },
  format_detail_value,
  format_field_label(value = '') {
    const text = String(value || '').trim().replace(/[_-]+/g, ' ');
    if (!text) return '-';
    return text.replace(/\b\w/g, (char) => char.toUpperCase());
  },
  status_badge_class(tone) {
    return manageVerificationSupportMethods.status_badge_class.call(this, tone);
  },
  evidence_icon_class(tone) {
    return manageVerificationSupportMethods.evidence_icon_class.call(this, tone);
  },
  field_result_row_class(match) {
    return manageVerificationSupportMethods.field_result_row_class.call(this, match);
  },
  format_match_status(value = '') {
    return manageVerificationSupportMethods.format_match_status.call(this, value);
  },
  normalize_match_status(value = '') {
    return manageVerificationSupportMethods.normalize_match_status.call(this, value);
  },
  history_verification_check_expanded(item = {}) {
    return this.expanded_history_verification_keys.includes(String(item?.id || ''));
  },
  toggle_history_verification_check_expanded(item = {}) {
    const target_key = String(item?.id || '');
    if (!target_key) return;
    this.expanded_history_verification_keys = this.history_verification_check_expanded(item)
      ? this.expanded_history_verification_keys.filter((key) => key !== target_key)
      : [...this.expanded_history_verification_keys, target_key];
  }
};
